import React, { useContext, useState, useEffect, createContext, useRef } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { ref, onValue, set, remove, onDisconnect } from 'firebase/database';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext({ currentUser: null, riderData: null, loading: true });

function LogoutNotification({ message, onAcknowledge }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: 'system-ui, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px 40px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0 5px 20px rgba(0,0,0,0.25)'
      }}>
        <p style={{ margin: '0 0 24px 0', fontSize: '17px', color: '#333' }}>{message}</p>
        <button onClick={onAcknowledge} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', backgroundColor: '#E3242B', color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '16px' }}>OK</button>
      </div>
    </div>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [riderData, setRiderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionListenerUnsubscribeRef = useRef(null);
  const [logoutNotification, setLogoutNotification] = useState(null);

  const signOut = async () => {
    if (currentUser) {
      const sessionRef = ref(db, `sessions/${currentUser.uid}`);
      onDisconnect(sessionRef).cancel();
      await remove(sessionRef);
    }
    // The onAuthStateChanged listener will handle clearing riderData.
    await firebaseSignOut(auth);
    localStorage.removeItem("sessionId");
  }

  const handleForcedLogout = async () => {
    setLogoutNotification(null);
    await signOut();
  };

  const signOutOtherDevices = async () => {
    if (!currentUser) return;

    try {
      const newDeviceId = uuidv4();
      const sessionRef = ref(db, `sessions/${currentUser.uid}`);
      
      // Update local storage for current device
      localStorage.setItem("deviceId", newDeviceId);
      
      // Update Firebase to invalidate other device sessions
      const sessionData = { sessionId: newDeviceId, timestamp: Date.now() };
      await set(sessionRef, sessionData);
      
      console.log("Successfully requested sign-out for other devices.");
    } catch (error) {
      console.error("Failed to sign out from other devices:", error);
    }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async user => {
      if (sessionListenerUnsubscribeRef.current) {
        sessionListenerUnsubscribeRef.current();
        sessionListenerUnsubscribeRef.current = null;
      }

      setCurrentUser(user);
      if (!user) {
        setRiderData(null);
        setLoading(false);
      } else {
        setLoading(true);
        // Reuse or create a persistent deviceId per browser device to avoid refresh false-positives
        let deviceId = localStorage.getItem("deviceId");
        if (!deviceId) {
          deviceId = uuidv4();
          localStorage.setItem("deviceId", deviceId);
        }

        const sessionRef = ref(db, `sessions/${user.uid}`);
        const sessionData = { sessionId: deviceId, timestamp: Date.now() };

        try {
          // Write current device session to Firebase FIRST
          await set(sessionRef, sessionData);

          // Attach onValue listener AFTER updating Firebase session
          sessionListenerUnsubscribeRef.current = onValue(sessionRef, (snapshot) => {
            const dbSessionId = snapshot.val()?.sessionId;
            const currentDeviceId = localStorage.getItem("deviceId");

            if (dbSessionId && currentDeviceId && dbSessionId !== currentDeviceId) {
              console.log("Another device has logged in. Signing out this device.");
              setLogoutNotification("You have been signed out because you signed in on another device.");
            }
          });
        } catch (error) {
          console.error("Could not set session data in Firebase", error);
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (sessionListenerUnsubscribeRef.current) {
        sessionListenerUnsubscribeRef.current();
      }
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      // Ensure data is cleared and we're not in a loading state if user logs out.
      setRiderData(null);
      return;
    }

    // currentUser exists, proceed to fetch data.
    // The loading state is already set to true by the onAuthStateChanged listener.
      const riderRef = ref(db, 'riders/' + currentUser.uid);
      const favoritesRef = ref(db, 'favorites/' + currentUser.uid);

      let riderInfo = null;
      let favoritesInfo = null;
      let riderListenerFired = false;
      let favoritesListenerFired = false;

      const tryCombineAndFinish = () => {
        // Only update state and finish loading once both listeners have fired at least once.
        if (riderListenerFired && favoritesListenerFired) {
          setRiderData({ ...(riderInfo || {}), favorites: favoritesInfo || {} });
          setLoading(false);
        }
      };

      const riderUnsubscribe = onValue(riderRef, (snapshot) => {
        riderInfo = snapshot.val();
        riderListenerFired = true;
        tryCombineAndFinish();
      }, (error) => {
        console.error("Error fetching rider data:", error);
        riderListenerFired = true;
        tryCombineAndFinish();
      });

      const favoritesUnsubscribe = onValue(favoritesRef, (snapshot) => {
        favoritesInfo = snapshot.val();
        favoritesListenerFired = true;
        tryCombineAndFinish();
      }, (error) => {
        console.error("Error fetching favorites data:", error);
        favoritesListenerFired = true;
        tryCombineAndFinish();
      });

      return () => {
        riderUnsubscribe();
        favoritesUnsubscribe();
      };
  }, [currentUser]);

  const value = {
    currentUser,
    riderData,
    signOut,
    signOutOtherDevices,
  };

  return (
    <AuthContext.Provider value={value}>
      {logoutNotification && (
        <LogoutNotification 
          message={logoutNotification} 
          onAcknowledge={handleForcedLogout} 
        />
      )}
      {!loading && children}
    </AuthContext.Provider>
  );
}